import React, { useState, KeyboardEvent } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { X } from 'lucide-react';

interface TagInputFieldProps {
  label: string;
  fieldName: string;
  placeholder: string;
  description?: string;
  form: any;
  setForm: React.Dispatch<React.SetStateAction<any>>;
  disabled?: boolean;
}

export const TagInputField = ({
  label,
  fieldName,
  placeholder,
  description,
  form,
  setForm,
  disabled = false,
}: TagInputFieldProps) => {
  const [inputValue, setInputValue] = useState('');

  // Get current tags from form data
  const currentValue = form?.[fieldName];
  const tags = Array.isArray(currentValue) ? currentValue : 
               typeof currentValue === 'string' && currentValue ? 
               currentValue.split(',').map((tag: string) => tag.trim()).filter(Boolean) : [];

  const addTag = (tag: string) => {
    const trimmedTag = tag.trim();
    if (!trimmedTag || disabled) return;

    if (!tags.includes(trimmedTag)) {
      const newTags = [...tags, trimmedTag];
      setForm((prev: any) => ({ ...prev, [fieldName]: newTags }));
    }
    setInputValue('');
  };

  const removeTag = (tagToRemove: string) => {
    if (disabled) return;
    const newTags = tags.filter((tag: string) => tag !== tagToRemove);
    setForm((prev: any) => ({ ...prev, [fieldName]: newTags }));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag(inputValue);
    } else if (e.key === 'Backspace' && inputValue === '' && tags.length > 0) {
      // Remove last tag when backspacing on empty input
      removeTag(tags[tags.length - 1]);
    } else if (e.key === 'Escape') {
      setInputValue('');
    }
  };

  return (
    <div className="space-y-2">
      <Label htmlFor={fieldName}>{label}</Label>
      
      {/* Display existing tags */}
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {tags.map((tag: string, index: number) => (
            <Badge key={index} variant="secondary" className="flex items-center gap-1">
              {tag}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => removeTag(tag)}
                  className="ml-1 hover:bg-destructive/20 rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </Badge>
          ))}
        </div>
      )}

      <Input
        id={fieldName}
        placeholder={placeholder}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          // Add tag on blur if there's content
          if (inputValue.trim() && !disabled) {
            addTag(inputValue);
          }
        }}
        disabled={disabled}
      />
      
      {description && (
        <p className="text-xs text-muted-foreground">
          {description}
        </p>
      )}
      
      {!disabled && (
        <p className="text-xs text-muted-foreground">
          Press Enter to add, click X to remove, Backspace to remove last
        </p>
      )}
    </div>
  );
};